// pages/Dashboard/Clients.js
import { useState, useMemo } from 'react';
import {
  FaSearch,
  FaUserPlus,
  FaMobileAlt,
  FaEnvelope,
  FaCalendarAlt,
  FaCut,
  FaTimes,
  FaMoneyBillWave,
  FaGift,
  FaEdit,
  FaHistory,
  FaTag,
  FaBirthdayCake,
} from 'react-icons/fa';
import '../Css/Clients.css';

const CLIENTS = [
  {
    id: 1,
    name: 'Priya Mehta',
    phone: '98765 43210',
    email: 'priya.mehta@example.com',
    tag: 'vip',
    birthday: '14 Mar',
    joined: 'Jan 2024',
    loyaltyPoints: 420,
    totalSpent: 18600,
    visits: 14,
    favoriteServices: ['Hair Color', 'Facial'],
    history: [
      { date: '12 Jul 2026', service: 'Hair Color', staff: 'Ankit Kapoor', amount: 1200 },
      { date: '02 Jun 2026', service: 'Facial', staff: 'Meena Iyer', amount: 900 },
      { date: '18 Apr 2026', service: 'Haircut', staff: 'Riya Sharma', amount: 300 },
    ],
    notes: 'Prefers ammonia-free hair color. Allergic to certain fragrances.',
  },
  {
    id: 2,
    name: 'Ankita Rao',
    phone: '98123 45678',
    email: 'ankita.rao@example.com',
    tag: 'regular',
    birthday: '02 Sep',
    joined: 'Aug 2024',
    loyaltyPoints: 180,
    totalSpent: 6400,
    visits: 6,
    favoriteServices: ['Manicure', 'Pedicure'],
    history: [
      { date: '10 Jul 2026', service: 'Manicure', staff: 'Sohail Khan', amount: 400 },
      { date: '01 May 2026', service: 'Pedicure', staff: 'Sohail Khan', amount: 500 },
    ],
    notes: '',
  },
  {
    id: 3,
    name: 'Sana Sheikh',
    phone: '99887 66554',
    email: 'sana.sheikh@example.com',
    tag: 'new',
    birthday: '27 Nov',
    joined: 'Jul 2026',
    loyaltyPoints: 20,
    totalSpent: 900,
    visits: 1,
    favoriteServices: ['Facial'],
    history: [{ date: '15 Jul 2026', service: 'Facial', staff: 'Meena Iyer', amount: 900 }],
    notes: 'First-time visitor, referred by Priya Mehta.',
  },
  {
    id: 4,
    name: 'Ritu Nair',
    phone: '90011 22334',
    email: 'ritu.nair@example.com',
    tag: 'inactive',
    birthday: '08 Jan',
    joined: 'Feb 2023',
    loyaltyPoints: 60,
    totalSpent: 3200,
    visits: 4,
    favoriteServices: ['Hair Spa'],
    history: [{ date: '20 Dec 2025', service: 'Hair Spa', staff: 'Riya Sharma', amount: 800 }],
    notes: 'Has not visited in over 6 months — consider a win-back offer.',
  },
];

const TAG_STYLES = {
  vip: { label: 'VIP', cls: 'tag-vip' },
  regular: { label: 'Regular', cls: 'tag-regular' },
  new: { label: 'New', cls: 'tag-new' },
  inactive: { label: 'Inactive', cls: 'tag-inactive' },
};

const FILTERS = [
  { key: 'all', label: 'All Clients' },
  { key: 'vip', label: 'VIP' },
  { key: 'new', label: 'New' },
  { key: 'inactive', label: 'Inactive' },
];

function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function Clients() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);

  const filteredClients = useMemo(() => {
    return CLIENTS.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.phone.includes(search);
      const matchesFilter = activeFilter === 'all' || c.tag === activeFilter;
      return matchesSearch && matchesFilter;
    });
  }, [search, activeFilter]);

  return (
    <div className="clients-page">
      {/* HEADER */}
      <div className="clients-header">
        <div>
          <h2>Clients</h2>
          <p>Manage customer profiles, history, and loyalty</p>
        </div>
        <button className="btn-primary">
          <FaUserPlus /> Add Client
        </button>
      </div>

      {/* STATS STRIP */}
      <div className="stats-strip">
        <div className="stat-card">
          <span className="stat-value">{CLIENTS.length}</span>
          <span className="stat-label">Total Clients</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {CLIENTS.filter((c) => c.tag === 'vip').length}
          </span>
          <span className="stat-label">VIP Clients</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {CLIENTS.filter((c) => c.tag === 'new').length}
          </span>
          <span className="stat-label">New This Month</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {CLIENTS.filter((c) => c.tag === 'inactive').length}
          </span>
          <span className="stat-label">Inactive</span>
        </div>
      </div>

      {/* SEARCH + FILTERS */}
      <div className="clients-toolbar">
        <div className="input-group search-input">
          <FaSearch className="input-icon" />
          <input
            type="text"
            placeholder="Search by name or phone number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`filter-chip ${activeFilter === f.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* CLIENTS TABLE (scrollable on all screen sizes) */}
      <div className="clients-table-wrapper">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact</th>
              <th>Tag</th>
              <th>Visits</th>
              <th>Total Spent</th>
              <th>Loyalty Points</th>
              <th>Favorite Services</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => (
              <tr
                key={client.id}
                className="client-row"
                onClick={() => setSelectedClient(client)}
              >
                <td>
                  <div className="table-client-name">
                    <div className="client-avatar small">{getInitials(client.name)}</div>
                    <div>
                      <strong>{client.name}</strong>
                      <span className="table-sub">Since {client.joined}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="table-contact">
                    <span><FaMobileAlt /> {client.phone}</span>
                    <span className="table-sub">{client.email}</span>
                  </div>
                </td>
                <td>
                  <span className={`client-tag ${TAG_STYLES[client.tag].cls}`}>
                    {TAG_STYLES[client.tag].label}
                  </span>
                </td>
                <td>{client.visits}</td>
                <td>₹{client.totalSpent.toLocaleString()}</td>
                <td>{client.loyaltyPoints}</td>
                <td>
                  <div className="client-favorites">
                    {client.favoriteServices.map((s) => (
                      <span key={s} className="fav-chip">
                        <FaCut /> {s}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <button
                    className="table-view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClient(client);
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}

            {filteredClients.length === 0 && (
              <tr>
                <td colSpan="8" className="clients-empty-row">
                  No clients match your search
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PROFILE DRAWER */}
      {selectedClient && (
        <div className="drawer-overlay" onClick={() => setSelectedClient(null)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="client-avatar large">
                  {getInitials(selectedClient.name)}
                </div>
                <div>
                  <h3>{selectedClient.name}</h3>
                  <span className={`client-tag ${TAG_STYLES[selectedClient.tag].cls}`}>
                    {TAG_STYLES[selectedClient.tag].label}
                  </span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setSelectedClient(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              {/* Contact Info */}
              <div className="drawer-section">
                <div className="drawer-section-head">
                  <h4>Contact Information</h4>
                  <button className="edit-link">
                    <FaEdit /> Edit
                  </button>
                </div>
                <div className="contact-info-list">
                  <div className="contact-info-item">
                    <FaMobileAlt /> {selectedClient.phone}
                  </div>
                  <div className="contact-info-item">
                    <FaEnvelope /> {selectedClient.email}
                  </div>
                  <div className="contact-info-item">
                    <FaBirthdayCake /> {selectedClient.birthday}
                  </div>
                  <div className="contact-info-item">
                    <FaCalendarAlt /> Client since {selectedClient.joined}
                  </div>
                </div>
              </div>

              {/* Summary stats */}
              <div className="drawer-stat-row">
                <div className="drawer-stat-box">
                  <FaMoneyBillWave className="drawer-stat-icon" />
                  <div>
                    <strong>₹{selectedClient.totalSpent.toLocaleString()}</strong>
                    <span>Total Spent</span>
                  </div>
                </div>
                <div className="drawer-stat-box">
                  <FaGift className="drawer-stat-icon" />
                  <div>
                    <strong>{selectedClient.loyaltyPoints}</strong>
                    <span>Loyalty Points</span>
                  </div>
                </div>
                <div className="drawer-stat-box">
                  <FaHistory className="drawer-stat-icon" />
                  <div>
                    <strong>{selectedClient.visits}</strong>
                    <span>Total Visits</span>
                  </div>
                </div>
              </div>

              {/* Favorites */}
              <div className="drawer-section">
                <h4>Favorite Services</h4>
                <div className="client-favorites">
                  {selectedClient.favoriteServices.map((s) => (
                    <span key={s} className="fav-chip">
                      <FaCut /> {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* Visit history */}
              <div className="drawer-section">
                <h4>Visit History</h4>
                <div className="history-timeline">
                  {selectedClient.history.map((h, i) => (
                    <div className="history-item" key={i}>
                      <div className="history-dot"></div>
                      <div className="history-content">
                        <div className="history-top-row">
                          <strong>{h.service}</strong>
                          <span className="history-amount">₹{h.amount}</span>
                        </div>
                        <div className="history-meta">
                          <FaCalendarAlt /> {h.date} &nbsp;•&nbsp; with {h.staff}
                        </div>
                      </div>
                    </div>
                  ))}
                  {selectedClient.history.length === 0 && (
                    <div className="history-empty">No visits recorded yet</div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="drawer-section">
                <div className="drawer-section-head">
                  <h4>
                    <FaTag /> Notes & Preferences
                  </h4>
                </div>
                <p className="notes-text">
                  {selectedClient.notes || 'No notes added for this client yet.'}
                </p>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width">
                <FaCalendarAlt /> Book Appointment
              </button>
              <button className="btn-primary full-width">
                <FaMoneyBillWave /> New Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;